import { lazy, Suspense, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useVendor } from "context/vendor/vendor.provider";
import {
  GET_CORPORATE_VALUES_BY_VENDOR,
  GET_SIMPLE_VENDOR_USER_LIST,
  ICorporateValueResponse,
  IVendorUserListResponse
} from "gql/CorporateValues/queries";
import { useAllUserStore } from "stores/recognition/useAllUserStore";
import { useCorporateStore } from "stores/recognition/useCorporateStore";

// Lazy load side panel widgets to reduce initial bundle
const AnniversaryWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/AnniversaryWidget")
);
const BannerWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/BannersWidget/BannersWidget")
);
const BirthdayWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/BirthdayWidget")
);
const CoinsRankingWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/CoinsRankingWidget")
);
const EndorsementWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/EndorsementWidget")
);
const RecognitionCorporateValueRankingWidget = lazy(
  () =>
    import(
      "components/_Pages/Explore/Widgets/RecognitionCorporateValueRankingWidget"
    )
);
const BuySellWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/BuySellWidget")
);
const MagazineWidget = lazy(
  () => import("components/_Pages/Explore/Widgets/MagazineWidget")
);
const ListExternalLink = lazy(
  () => import("components/ui/listExternalLink/listExternalLink")
);
const BenefitsWidget = lazy(
  () => import("components/ui/benefitsWidget/benefitsWidget")
);

interface LazySidePanelProps {
  render: boolean;
  className?: string;
  vendor?: any;
}

const WidgetSkeleton = () => (
  <div
    className="w-full rounded-[22px] border border-slate-200/80 bg-white p-4 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.04)] dark:border-zinc-800/60 dark:bg-zinc-900"
    role="status"
    aria-label="Loading widget"
  >
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="relative h-4 w-4 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
        </div>
        <div className="relative h-3 w-28 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
        </div>
      </div>
      <div className="relative h-7 w-7 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
      </div>
    </div>
    <div className="space-y-2">
      <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
      </div>
      <div className="relative h-3 w-5/6 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
      </div>
      <div className="relative h-3 w-4/6 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
      </div>
    </div>
  </div>
);

export default function LazySidePanel(props: LazySidePanelProps) {
  const { render = true, vendor } = props;
  const { vendor: vendorContext } = useVendor();

  const {
    dataCorporate,
    setLoadingCorporate,
    setErrorCorporate,
    setDataCorporate
  } = useCorporateStore();

  const { dataAllUser, setDataAllUser, setLoadingAllUser, setErrorAllUser } =
    useAllUserStore();

  // Fetch corporate values
  const { loading: loadingCorporate } = useQuery<ICorporateValueResponse>(
    GET_CORPORATE_VALUES_BY_VENDOR,
    {
      fetchPolicy: "cache-first",
      skip: !render || !vendorContext || Boolean(dataCorporate),
      onCompleted: (data) => {
        setDataCorporate(data);
      },
      onError: (error) => {
        setErrorCorporate(error);
      }
    }
  );

  useEffect(() => {
    setLoadingCorporate(loadingCorporate);
  }, [loadingCorporate, setLoadingCorporate]);

  // Fetch user data
  const { loading: loadingUser } = useQuery<IVendorUserListResponse>(
    GET_SIMPLE_VENDOR_USER_LIST,
    {
      fetchPolicy: "cache-first",
      skip: !render || !vendorContext || Boolean(dataAllUser),
      onCompleted: (data) => {
        setDataAllUser(data);
      },
      onError: (error) => {
        setErrorAllUser(error);
      }
    }
  );

  useEffect(() => {
    setLoadingAllUser(loadingUser);
  }, [loadingUser, setLoadingAllUser]);

  if (!render) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-col">
      <div
        className={`mt-6 flex min-w-0 flex-col gap-3 ${props.className ?? ""}`}
      >
        {vendor?.enableBirthdayAndAnniversary && (
          <>
            <Suspense fallback={<WidgetSkeleton />}>
              <BirthdayWidget variant="v2" />
            </Suspense>
            <Suspense fallback={<WidgetSkeleton />}>
              <AnniversaryWidget variant="v2" />
            </Suspense>
            <Suspense fallback={<WidgetSkeleton />}>
              <EndorsementWidget variant="v2" />
            </Suspense>
          </>
        )}

        {vendor?.enableCorporateValue && (
          <Suspense fallback={<WidgetSkeleton />}>
            <RecognitionCorporateValueRankingWidget variant="v2" />
          </Suspense>
        )}

        {vendor?.enableBuySell && (
          <Suspense fallback={<WidgetSkeleton />}>
            <BuySellWidget variant="v2" />
          </Suspense>
        )}

        {vendor?.enableMagazine && (
          <Suspense fallback={<WidgetSkeleton />}>
            <MagazineWidget variant="v2" />
          </Suspense>
        )}

        {vendor?.enableCoins && (
          <Suspense fallback={<WidgetSkeleton />}>
            <CoinsRankingWidget variant="v2" />
          </Suspense>
        )}

        <Suspense fallback={<WidgetSkeleton />}>
          <BannerWidget clean variant={"info"} />
        </Suspense>

        <Suspense fallback={<WidgetSkeleton />}>
          <ListExternalLink variant="v2" />
        </Suspense>

        {vendor?.enableBenefits && (
          <Suspense fallback={<WidgetSkeleton />}>
            <BenefitsWidget variant="v2" />
          </Suspense>
        )}

        {vendor?.enableCoins && (
          <Suspense fallback={<WidgetSkeleton />}>
            <BannerWidget variant={"events"} />
          </Suspense>
        )}
      </div>
    </div>
  );
}
